/**
 * Email formatter — the canonical spacing/structure pipeline for every
 * SafeAttack `bodyHtml` that enters the system. Pure, deterministic, idempotent.
 *
 * Wired into createSafeAttack / updateSafeAttack so no generation entry point
 * (generate, generate/chat, submit, direct POST, seed scripts) can bypass it.
 *
 * Scope: normalizes STRUCTURE, not prose. The LLM prompt is responsible for
 * natural English; this file ensures the emitted HTML renders with correct
 * paragraph separation, clean signature, no duplicated greeting.
 */

const SIGNOFF_PATTERNS = [
  'Best regards',
  'Kind regards',
  'Warm regards',
  'Regards',
  'Sincerely',
  'Thanks',
  'Thank you',
  'Best',
  'Cheers',
  'Yours sincerely',
  'Yours truly',
]

const SIGNOFF_RE = new RegExp(
  `^\\s*(?:${SIGNOFF_PATTERNS.map((s) => s.replace(/\s+/g, '\\s+')).join('|')})[,.]`,
  'i',
)

export interface FormatEmailBodyInput {
  bodyHtml: string
  greeting?: string
  fromName?: string
}

export interface FormatEmailBodyResult {
  bodyHtml: string
  /**
   * Ongoing quality warnings — conditions that are STILL TRUE after formatting
   * (e.g. TOO_FEW_PARAGRAPHS, EMPTY_BODY). Persisted on the record for admin review.
   */
  warnings: string[]
  /**
   * Actions the formatter took this run (e.g. STRIPPED_DUPLICATE_GREETING,
   * DROPPED_EMPTY_P, WRAPPED_RAW_TEXT). NOT persisted — action logs aren't useful
   * historically since the condition is already resolved. Kept on the in-memory
   * result for CLI/debug reporting.
   */
  actions: string[]
  stats: {
    paragraphs: number
    hasSignature: boolean
    hasCta: boolean
  }
}

export function formatEmailBody(input: FormatEmailBodyInput): FormatEmailBodyResult {
  const warnings: string[] = []
  const actions: string[] = []
  let html = (input.bodyHtml || '').trim()

  if (!html) {
    return {
      bodyHtml: '',
      warnings: ['EMPTY_BODY'],
      actions: [],
      stats: { paragraphs: 0, hasSignature: false, hasCta: false },
    }
  }

  // Pass 1: trim + collapse runs of whitespace between tags.
  html = html.replace(/>\s+</g, '><')

  // Pass 3 (done first so later passes see real <p> structure):
  // If bodyHtml has no <p> tags at all, wrap into paragraphs on blank-line / <br><br> boundaries.
  if (!/<p[\s>]/i.test(html)) {
    actions.push('WRAPPED_RAW_TEXT')
    const chunks = html
      .split(/(?:\r?\n\s*\r?\n|<br\s*\/?>\s*<br\s*\/?>)/i)
      .map((c) => c.trim())
      .filter(Boolean)
    html = chunks.map((c) => `<p>${c}</p>`).join('')
  }

  // Pass 6: drop empty <p></p> (including whitespace-only or &nbsp;-only).
  {
    const before = html
    html = html.replace(/<p(\s[^>]*)?>\s*(?:&nbsp;|\s)*<\/p>/gi, '')
    if (html !== before) actions.push('DROPPED_EMPTY_P')
  }

  // Pass 2: strip duplicated greeting.
  if (input.greeting) {
    const greetingText = input.greeting.trim().replace(/[,.:!]+$/, '')
    if (greetingText) {
      // Normalize patterns: "Dear Arjun," "Hi Arjun," "Hello,"
      const escaped = escapeRegExp(greetingText)
      // Case a: first <p> contains ONLY the greeting (possibly with trailing ,).
      const soloRe = new RegExp(
        `^\\s*<p(\\s[^>]*)?>\\s*${escaped}\\s*[,.:!]?\\s*<\\/p>`,
        'i',
      )
      if (soloRe.test(html)) {
        html = html.replace(soloRe, '')
        actions.push('STRIPPED_DUPLICATE_GREETING')
      } else {
        // Case b: first <p> STARTS with greeting + comma + space, then content.
        const leadRe = new RegExp(
          `^(\\s*<p(\\s[^>]*)?>)\\s*${escaped}\\s*[,.:!]\\s*`,
          'i',
        )
        if (leadRe.test(html)) {
          html = html.replace(leadRe, '$1')
          actions.push('STRIPPED_LEADING_GREETING_TEXT')
        }
      }
      html = html.trim()
    }
  }

  // Pass 5: tag CTA paragraphs (center-aligned <p>). Idempotent — skip if class already present.
  html = html.replace(/<p\s+([^>]*?)>/gi, (match, attrs: string) => {
    if (!/style\s*=/i.test(attrs)) return match
    if (!/text-align\s*:\s*center/i.test(attrs)) return match
    if (/class\s*=\s*"[^"]*pt-email-cta/i.test(attrs)) return match
    if (/class\s*=\s*"([^"]*)"/i.test(attrs)) {
      const merged = attrs.replace(
        /class\s*=\s*"([^"]*)"/i,
        (_m: string, existing: string) => `class="${(existing + ' pt-email-cta').trim()}"`,
      )
      return `<p ${merged}>`
    }
    return `<p ${attrs} class="pt-email-cta">`
  })

  // Pass 4: tag signature paragraph (last <p> whose text starts with a signoff).
  // We mark it for the renderer to add extra top gap.
  {
    const pMatches = Array.from(html.matchAll(/<p(\s[^>]*)?>([\s\S]*?)<\/p>/gi))
    if (pMatches.length > 0) {
      // Walk backwards to find a signoff paragraph. Often last; sometimes last-1 if a footer exists.
      for (let i = pMatches.length - 1; i >= 0; i--) {
        const match = pMatches[i]
        const innerText = stripTags(match[2] || '').trim()
        if (SIGNOFF_RE.test(innerText)) {
          const original = match[0]
          const attrs = match[1] || ''
          // Skip if already tagged.
          if (/class\s*=\s*"[^"]*pt-email-signature/i.test(attrs)) break
          let newAttrs = attrs
          if (/class\s*=\s*"([^"]*)"/i.test(attrs)) {
            newAttrs = attrs.replace(
              /class\s*=\s*"([^"]*)"/i,
              (_m: string, existing: string) =>
                `class="${(existing + ' pt-email-signature').trim()}"`,
            )
          } else {
            newAttrs = `${attrs} class="pt-email-signature"`
          }
          const replacement = `<p${newAttrs}>${match[2]}</p>`
          html = html.replace(original, replacement)
          break
        }
      }
    }
  }

  // Pass 8: serialize with one \n between top-level <p> for git-diff readability.
  html = html.replace(/<\/p>\s*<p/gi, '</p>\n<p').trim()

  // Compute stats.
  const pCount = (html.match(/<p[\s>]/gi) || []).length
  const hasSignature = /class\s*=\s*"[^"]*pt-email-signature/i.test(html)
  const hasCta = /class\s*=\s*"[^"]*pt-email-cta/i.test(html)

  // Pass 7: minimum structure check (warning only, not rejection).
  if (pCount < 2) warnings.push('TOO_FEW_PARAGRAPHS')

  return {
    bodyHtml: html,
    warnings,
    actions,
    stats: { paragraphs: pCount, hasSignature, hasCta },
  }
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function stripTags(s: string): string {
  return s.replace(/<[^>]+>/g, '')
}

/**
 * Remove a greeting that is DUPLICATED at the top of `bodyHtml` when the
 * greeting is also rendered from its own field (2026-07-14).
 *
 * Every SafeAttack stores the greeting in its own column AND renders it above
 * the body. Two seeded records also repeat it as the first paragraph, so every
 * surface that prints `greeting` + raw `bodyHtml` showed it twice ("Hi Sarah,"
 * / "Hi Sarah," / "As discussed…"). The play adapter already strips it, which
 * is why the game looked fine while the library/report/AI-gen previews did not.
 *
 * Why not reuse formatEmailBody's pass-2: that one is anchored to `^\s*<p`, so
 * it MISSES the real data — every seeded body opens with a `<div style=…>`
 * wrapper before the first `<p>`. This finds the first paragraph wherever it
 * sits and is therefore wrapper-tolerant.
 *
 * Idempotent + safe: with no greeting, no match, or a body that does not start
 * with it, the html is returned untouched.
 */
export function stripDuplicateGreeting(
  bodyHtml: string,
  greeting?: string | null,
): string {
  if (!bodyHtml || !greeting) return bodyHtml

  const norm = (s: string) =>
    stripTags(s)
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/[,.:!;]+$/, '')
      .toLowerCase()

  const target = norm(greeting)
  if (!target) return bodyHtml

  // First paragraph anywhere in the html (wrapper divs/tables tolerated).
  const firstP = /<p(\s[^>]*)?>([\s\S]*?)<\/p>/i.exec(bodyHtml)
  if (!firstP) return bodyHtml

  const inner = firstP[2] ?? ''
  const innerNorm = norm(inner)

  // Case a — the paragraph IS the greeting: drop the whole <p>…</p>.
  if (innerNorm === target) {
    return bodyHtml.replace(firstP[0], '').replace(/^\s*[\r\n]+/, '')
  }

  // Case b — the paragraph STARTS with the greeting, then real content:
  // strip just the greeting prefix, keep the paragraph.
  if (innerNorm.startsWith(`${target} `) || innerNorm.startsWith(`${target},`)) {
    const escaped = escapeRegExp(greeting.trim().replace(/[,.:!;]+$/, ''))
    const cleanedInner = inner.replace(
      new RegExp(`^\\s*(?:<[^>]+>\\s*)*${escaped}\\s*[,.:!;]?\\s*(?:<br\\s*/?>)?\\s*`, 'i'),
      '',
    )
    if (cleanedInner !== inner) {
      return bodyHtml.replace(firstP[0], firstP[0].replace(inner, cleanedInner))
    }
  }

  return bodyHtml
}
